
import { Credentials } from "auth/modules/state";
import React, { FC, useMemo } from "react";
import { useSelector } from "react-redux";
import { Redirect, useLocation, useParams } from "react-router-dom";
import { State } from "redux/state";
import { useProfile } from "utils/useProfile";
import {
    ProtectedRoute,
    ProtectedRouteProps
} from "../components/ProtectedRoute";

interface Params {
    integrationId?: string;
    appId?: string;
    organizationId?: string;
}

export interface ProtectedRouteContainerProps extends Omit<ProtectedRouteProps, "profile" | "credentials"> {
    overrides?: {
        signUp: {
            pathname: string;
            search?: string;
        };
        login: {
            pathname: string;
            search?: string;
        }
    }
}

/**
 * Wrapper around Route that ensure the user is logged in.
 */
export const ProtectedRouteContainer: FC<ProtectedRouteContainerProps> = props => {

    const profile = useProfile();
    const credentials = useSelector<State, Credentials>(state => state.auth.credentials);
    const location = useLocation();
    const params = useParams<Params>();

    const loginPath: { pathname: string, search?: string } = useMemo(() => {
        return props?.overrides?.login || { pathname: "/login" };
    }, [props?.overrides?.login]);

    const signUpPath: { pathname: string, search?: string } = useMemo(() => {
        // change it depending on environment
        const signUpPathName = window.xaStudioConfig.account === "studio" ? "/onboarding/sign-up" : "/sign-up";
        return props?.overrides?.signUp || { pathname: signUpPathName }
    }, [props?.overrides?.signUp]);

    const redirectToAuth = useMemo(() => {

        // If we don't have previous credentials, we will redirect them to the sign up page
        // if we just have expired credentials, we will redirect them to the login page
        // TODO: we want to also look at the original requested path and redirect based on the requested URL
        //  1. if it is {orgId}/{appId}/upgrade, redirect to login
        //  2. Or if they are requesting any path with {orgId}/{appId}, we redirect them to login
        const mode = !credentials ? signUpPath : (new Date(credentials.Expiration).getTime() <= new Date().getTime() ? loginPath : null);

        // TODO: Remove these when we figure out our login issues
        if (!credentials) {
            console.log(`Credentials don't yet exist...`);
        }

        if (credentials && new Date(credentials.Expiration).getTime() <= new Date().getTime()) {
            console.log(`Credentials expired.`);
        }

        const pathname = location.pathname;

        const searchParams = new URLSearchParams(location.search);
        if (params?.integrationId) {
            searchParams.set("partner", params?.integrationId)
        }

        if (mode) {

            const search = searchParams.toString();
            mode.search = search;

            console.log(`Redirecting to ${mode.pathname} with search ${search}`);

            return (
                <Redirect
                    to={{
                        ...mode,
                        state: {
                            from: {
                                pathname,
                                search
                            }
                        }
                    }}
                />
            );
        }
        return null;
    }, [credentials, location.pathname, location.search, loginPath, params?.integrationId, signUpPath]);

    return redirectToAuth || <ProtectedRoute profile={profile} credentials={credentials} {...props} />;
};
